import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
} from "reactstrap";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Perhatikan huruf 'T' kapital pada 'Toastify'

import { useLocation, useNavigate } from 'react-router-dom';

import { tryoutGetPembahasan } from '../../../services/tryoutService';
import Spinners from "components/Common/Spinner";

// Fungsi helper untuk mendekode entitas HTML
const decodeHtmlEntities = (htmlString) => {
  if (!htmlString) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = htmlString;
  return textarea.value;
};

const Pembahasan = () => {
  document.title = "Pembahasan Tryout";

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const idTryoutAnswer = queryParams.get('id_tryout_answer');
  const idTipeKonten = queryParams.get('id_tipe_konten');
  const idJenisSeleksi = queryParams.get('id_jenis_seleksi');

  const [pembahasanData, setPembahasanData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPembahasanData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!idTipeKonten && !idTryoutAnswer) {
        throw new Error("Parameter 'id_tryout_answer' atau id_tipe_konten tidak ditemukan di URL. Pastikan URL benar.");
      }
      const response = await tryoutGetPembahasan(idTryoutAnswer, idJenisSeleksi);
      if (response?.success && response.data) {
        // Decode HTML entities for soal, options, and pembahasan when data is fetched
        const decodedData = response.data.map(item => ({
          ...item,
          soal: decodeHtmlEntities(item.soal),
          opsi_a: decodeHtmlEntities(item.opsi_a),
          opsi_b: decodeHtmlEntities(item.opsi_b),
          opsi_c: decodeHtmlEntities(item.opsi_c),
          opsi_d: decodeHtmlEntities(item.opsi_d),
          opsi_e: decodeHtmlEntities(item.opsi_e),
          pembahasan: decodeHtmlEntities(item.pembahasan),
        }));
        setPembahasanData(decodedData);
      } else {
        throw new Error(response?.message || "Gagal memuat data pembahasan tryout.");
      }
    } catch (err) {
      console.error("Error fetching pembahasan data:", err);
      setError(err.message || "Terjadi kesalahan saat memuat pembahasan tryout.");
      toast.error(err.message || "Gagal memuat data pembahasan.");
    } finally {
      setIsLoading(false);
    }
  }, [idTryoutAnswer]);

  useEffect(() => {
    if (idTryoutAnswer || idTipeKonten) {
      fetchPembahasanData();
    } else {
      setIsLoading(false);
      setError("Parameter 'id_tryout_answer' tidak ditemukan di URL.");
      toast.error("URL tidak valid. Parameter id_tryout_answer diperlukan.");
    }
  }, [fetchPembahasanData, idTryoutAnswer]);

  const handleNextQuestion = useCallback(() => {
    if (pembahasanData && currentQuestionIndex < pembahasanData.length - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
      window.scrollTo(0, 0);
    }
  }, [currentQuestionIndex, pembahasanData]);

  const handlePreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
      window.scrollTo(0, 0);
    }
  }, [currentQuestionIndex]);

  const handleQuestionNavigation = useCallback((index) => {
    setCurrentQuestionIndex(index);
    window.scrollTo(0, 0);
  }, []);

  const customColors = {
    bgLightGray: '#f8f9fa',
    bgWhite: '#ffffff',
    borderColor: '#e0e0e0',
    primaryBlue: '#007bff',
    successGreen: '#28a745',
    dangerRed: '#dc3545',
    warningOrange: '#fd7e14',
    infoBlue: '#17a2b8',
    textDark: '#343a40',
    textMuted: '#6c757d',
    cardShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
  };

  if (isLoading) {
    return (
      <div className="page-content d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '80vh', backgroundColor: customColors.bgLightGray }}>
        <Spinners color="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-2 text-primary fw-bold">Memuat pembahasan soal, mohon tunggu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content d-flex flex-column justify-content-center align-items-center bg-light" style={{ minHeight: '80vh' }}>
        <i className="bx bx-error text-danger display-4 mb-3"></i>
        <p className="text-danger font-size-18 text-center">{error}</p>
      </div>
    );
  }

  if (!pembahasanData || pembahasanData.length === 0) {
    return (
      <div className="page-content d-flex flex-column justify-content-center align-items-center bg-light" style={{ minHeight: '80vh' }}>
        <i className="bx bx-file-blank text-info display-4 mb-3"></i>
        <p className="font-size-18">Tidak ada data pembahasan untuk tryout ini.</p>

      </div>
    );
  }

  const currentQuestion = pembahasanData[currentQuestionIndex];
  const totalQuestions = pembahasanData.length;

  return (
    <React.Fragment>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="page-content" style={{ backgroundColor: customColors.bgLightGray }}>
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
            <h5 className="mb-0 fw-bold" style={{ color: customColors.textDark }}>PEMBAHASAN TRYOUT</h5>
            <div className="d-flex align-items-center">
              <span className="text-muted me-2">Tryout / Pembahasan</span>
            </div>
          </div>

          <Row className="g-3">
            <Col lg={9}>
              <Card className="border-0" style={{ backgroundColor: customColors.bgWhite, borderRadius: '8px', boxShadow: customColors.cardShadow }}>
                <CardBody className="p-3">
                  <div className="d-flex justify-content-between align-items-center pb-2 mb-3" style={{ borderBottom: `1px solid ${customColors.borderColor}` }}>
                    <CardTitle className="h5 mb-0 d-flex align-items-center" style={{ color: customColors.textDark }}>
                      Soal Nomor {currentQuestionIndex + 1}
                    </CardTitle>
                    <Badge
                      className="p-2 font-size-12"
                      style={{
                        backgroundColor: currentQuestion.jenis_soal === 'TWK' ? customColors.primaryBlue :
                          currentQuestion.jenis_soal === 'TIU' ? customColors.warningOrange :
                            currentQuestion.jenis_soal === 'TKP' ? customColors.successGreen : customColors.infoBlue,
                        color: '#fff',
                        borderRadius: '4px'
                      }}
                    >
                      {currentQuestion.jenis_soal || 'Umum'}
                    </Badge>
                  </div>

                  {currentQuestion.image_soal && (
                    <div className="mb-3 text-center p-2 rounded" style={{ backgroundColor: customColors.bgLightGray, borderRadius: '6px' }}>
                      <img
                        src={currentQuestion.image_soal}
                        alt="Gambar Soal"
                        className="img-fluid rounded"
                        style={{ maxWidth: '100%', maxHeight: '250px', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; console.warn("Gambar soal tidak ditemukan atau gagal dimuat:", e.target.src); }}
                      />
                      <small className="text-muted mt-1 d-block">Gambar Soal</small>
                    </div>
                  )}
                  <p className="font-size-15 mb-3 lh-base" style={{ color: customColors.textDark }} dangerouslySetInnerHTML={{ __html: currentQuestion.soal }}></p>

                  <div className="options-container mb-3">
                    {['A', 'B', 'C', 'D', 'E'].map(optionChar => {
                      const optionKey = `opsi_${optionChar.toLowerCase()}`;
                      const optionText = currentQuestion[optionKey];
                      const optionImage = currentQuestion[`image_${optionKey}`];

                      if (optionText === undefined || optionText === null || optionText === '') {
                        return null;
                      }

                      const isCorrect = optionChar === currentQuestion.jawaban_benar;
                      const isUserAnswer = optionChar === currentQuestion.jawaban_peserta;

                      let backgroundColor = customColors.bgWhite;
                      let borderColor = customColors.borderColor;
                      let textColor = customColors.textDark;
                      let icon = null;
                      let tagText = '';
                      let tagColor = '';

                      if (isCorrect && isUserAnswer) {
                        backgroundColor = `${customColors.successGreen}10`;
                        borderColor = customColors.successGreen;
                        textColor = customColors.successGreen;
                        icon = <i className="bx bx-check-circle me-1" style={{ color: customColors.successGreen }}></i>;
                        tagText = 'Jawaban Anda Benar';
                        tagColor = 'success';
                      } else if (isUserAnswer && !isCorrect) {
                        backgroundColor = `${customColors.dangerRed}10`;
                        borderColor = customColors.dangerRed;
                        textColor = customColors.dangerRed;
                        icon = <i className="bx bx-x-circle me-1" style={{ color: customColors.dangerRed }}></i>;
                        tagText = 'Jawaban Anda Salah';
                        tagColor = 'danger';
                      } else if (isCorrect && !isUserAnswer) {
                        backgroundColor = `${customColors.primaryBlue}10`;
                        borderColor = customColors.primaryBlue;
                        textColor = customColors.primaryBlue;
                        icon = <i className="bx bx-info-circle me-1" style={{ color: customColors.primaryBlue }}></i>;
                        tagText = 'Jawaban Benar';
                        tagColor = 'info';
                      }

                      return (
                        <div
                          key={optionKey}
                          className="d-flex align-items-start py-2 px-3 mb-2 border rounded"
                          style={{
                            backgroundColor: backgroundColor,
                            borderColor: borderColor,
                            borderWidth: '1px',
                            borderRadius: '8px',
                            transition: 'all 0.15s ease-in-out',
                          }}
                        >
                          <div className="flex-shrink-0 me-2 mt-1">
                            {icon}
                          </div>
                          <div className="flex-grow-1" style={{ color: textColor, fontSize: '0.9rem' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-bold me-1">{optionChar}.</span>
                              {tagText && (
                                <Badge color={tagColor} className="ms-auto py-1 px-2 font-size-10">
                                  {tagText}
                                </Badge>
                              )}
                            </div>
                            <div dangerouslySetInnerHTML={{ __html: optionText }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 pt-3 border-top" style={{ borderColor: customColors.borderColor }}>
                  {Number(currentQuestion?.id_tipe_konten) !== 4 && (
                        <h6 className="fw-bold mb-2" style={{ color: customColors.textDark }}>
                          Jawaban Anda:
                          <Badge
                            color={
                              currentQuestion.jawaban_peserta === currentQuestion.jawaban_benar
                                ? "success"
                                : currentQuestion.jawaban_peserta === 'N'
                                ? "secondary"
                                : "danger"
                            }
                            className="ms-2 font-size-14 py-2 px-3"
                          >
                            {currentQuestion.jawaban_peserta === 'N'
                              ? "Anda Belum Menjawab"
                              : currentQuestion.jawaban_peserta || "Tidak Dijawab"}
                          </Badge>
                        </h6>
                      )}


                      <h6 className="fw-bold mb-2" style={{ color: customColors.textDark }}>Jawaban Benar:
                        <Badge color="info" className="ms-2 font-size-14 py-2 px-3">
                          {currentQuestion.jawaban_benar}
                        </Badge>
                      </h6>

                      <h6 className="fw-bold mt-4 mb-2" style={{ color: customColors.textDark }}>Pembahasan:
                        {currentQuestion.pembahasan && (
                          <Badge color="primary" className="ms-2 py-1 px-2 font-size-10">
                            Tersedia
                          </Badge>
                        )}
                      </h6>
                      {currentQuestion.pembahasan ? (
                        <p className="font-size-14 lh-base" style={{ color: customColors.textDark }} dangerouslySetInnerHTML={{ __html: currentQuestion.pembahasan }}></p>
                      ) : (
                        <p className="text-muted font-size-14">Tidak ada pembahasan tersedia untuk soal ini.</p>
                      )}
                    </div>

                  <div className="d-flex justify-content-between mt-3 pt-2" style={{ borderTop: `1px solid ${customColors.borderColor}` }}>
                    <Button
                      color="secondary"
                      outline
                      onClick={handlePreviousQuestion}
                      disabled={currentQuestionIndex === 0}
                      className="btn-md px-3"
                      style={{ minWidth: '100px', borderColor: customColors.textMuted, color: customColors.textMuted, borderRadius: '4px', fontSize: '0.875rem' }}
                    >
                      <i className="bx bx-chevron-left me-1"></i> Sebelumnya
                    </Button>
                    <Button
                      color="primary"
                      onClick={handleNextQuestion}
                      disabled={currentQuestionIndex === totalQuestions - 1}
                      className="btn-md px-3"
                      style={{ minWidth: '100px', backgroundColor: customColors.primaryBlue, borderColor: customColors.primaryBlue, borderRadius: '4px', fontSize: '0.875rem' }}
                    >
                      Selanjutnya <i className="bx bx-chevron-right ms-1"></i>
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              <div className="d-flex flex-column g-3">
                {/* Bagian Progress Pembahasan */}
                <Card className="border-0" style={{ backgroundColor: customColors.bgWhite, borderRadius: '8px', boxShadow: customColors.cardShadow }}>
                  <CardBody className="p-3">
                    <CardTitle className="h6 mb-3 fw-bold" style={{ color: customColors.textDark }}>
                      Progress Pembahasan
                      <span className="float-end text-muted fw-normal font-size-13">
                        {currentQuestionIndex + 1}/{totalQuestions} Soal
                      </span>
                    </CardTitle>
                    <div className="progress mb-3" style={{ height: '8px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%`, backgroundColor: customColors.primaryBlue }}
                        aria-valuenow={((currentQuestionIndex + 1) / totalQuestions) * 100}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </CardBody>
                </Card>

                {/* Bagian Navigasi Soal */}
                <Card className="border-0" style={{ backgroundColor: customColors.bgWhite, borderRadius: '8px', boxShadow: customColors.cardShadow }}>
                  <CardBody className="p-3">
                    <CardTitle className="h6 mb-3 fw-bold" style={{ color: customColors.textDark }}>
                      Navigasi Soal
                    </CardTitle>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)', // Memaksa 5 kolom dengan lebar yang sama
                        gap: '0.2rem', // <<< GAP DIKECILKAN LAGI (dari 0.3rem)
                        maxHeight: '300px',
                        overflowY: 'auto',
                        overflowX: 'hidden', // Mencegah scroll horizontal
                        // paddingRight: '8px' telah dihapus
                      }}
                    >
                      {pembahasanData.map((q, index) => {
                         const isActive = index === currentQuestionIndex;

                        return (
                          <Button
                            key={q.id_soal}
                            className="fw-bold"
                            style={{
                              height: '36px', // <<< HEIGHT TOMBOL DIKECILKAN LAGI (dari 38px)
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.8rem', // <<< FONT SIZE DIKECILKAN LAGI (dari 0.85rem)
                              borderRadius: '6px',
                              backgroundColor: isActive ? customColors.primaryBlue : customColors.bgWhite,
                              borderColor: isActive ? customColors.primaryBlue : customColors.borderColor,
                              color: isActive ? '#fff' : customColors.textDark,
                              boxShadow: isActive ? '0 2px 4px rgba(0, 123, 255, 0.2)' : 'none',
                              transition: 'all 0.1s ease-in-out',
                            }}
                            onClick={() => handleQuestionNavigation(index)}
                          >
                            {index + 1}
                          </Button>
                        );
                      })}
                    </div>
                  </CardBody>
                </Card>

                {/* Bagian Selesai Pembahasan / Kembali */}
                {/* <Card className="border-0" style={{ backgroundColor: customColors.bgWhite, borderRadius: '8px', boxShadow: customColors.cardShadow }}>
                  <CardBody className="p-3 text-center">
                    <Button
                      color="primary"
                      className="w-100 btn-md"
                      onClick={() => navigate('/paket-simulasi?kategori_paket=TRYOUT')}
                      style={{ borderRadius: '6px', fontSize: '1rem', padding: '10px 15px' }}
                    >
                      Kembali ke Paket Simulasi
                    </Button>
                  </CardBody>
                </Card> */}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Pembahasan;